package dev.lingvo.site.components.marketing

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.minHeight
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import dev.lingvo.site.ds.IconSize
import dev.lingvo.site.ds.HIT_AREA_FLOOR_PX
import dev.lingvo.site.ds.LChevronDownIcon
import dev.lingvo.site.ds.Motion
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.iconSize
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tr
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.ElementBuilder
import org.jetbrains.compose.web.dom.Nav
import org.jetbrains.compose.web.dom.Ul
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.TagElement
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement
import org.w3c.dom.Node
import org.w3c.dom.events.EventListener
import org.w3c.dom.events.MouseEvent

/** A stable deep link for a command or tool within a documentation section. */
data class DocAnchor(val id: String, val label: String)

private val DetailsElement = ElementBuilder.createBuilder<HTMLElement>("details")
private val SummaryElement = ElementBuilder.createBuilder<HTMLElement>("summary")

@Composable
internal fun rememberDocSection(entries: List<DocEntry>): String? {
    val anchors = entries.flatMap { entry -> listOf(entry.id) + entry.children.map { it.id } }
    val inventory = anchors.joinToString("|")
    var current by remember(inventory) { mutableStateOf(anchors.firstOrNull()) }
    DisposableEffect(inventory) {
        var frame: Int? = null
        val update = {
            val sections = anchors.mapNotNull { document.getElementById(it) as? HTMLElement }
            val reached = sections.lastOrNull { it.getBoundingClientRect().top <= anchorOffset(it) + 16 }
            current = reached?.id ?: anchors.firstOrNull()
        }
        val listener = EventListener {
            if (frame == null) frame = window.requestAnimationFrame {
                frame = null
                update()
            }
        }
        update()
        window.addEventListener("scroll", listener)
        window.addEventListener("resize", listener)
        window.addEventListener("hashchange", listener)
        onDispose {
            frame?.let(window::cancelAnimationFrame)
            window.removeEventListener("scroll", listener)
            window.removeEventListener("resize", listener)
            window.removeEventListener("hashchange", listener)
        }
    }
    return current
}

@Composable
internal fun DocsNavigation(entries: List<DocEntry>, current: String?) {
    val activeLabel = entries.flatMap { listOf(DocAnchor(it.id, it.label)) + it.children }
        .firstOrNull { it.id == current }?.label.orEmpty()
    Nav(DocsRailStyle.toModifier().toAttrs {
        attr("aria-label", tr("marketing.docs_navigation.on_this_page", "On this page"))
    }) {
        Column(Modifier.fillMaxWidth().gap(Space.SM)) {
            Span(Modifier.padding(leftRight = Space.SM).fontSize(TypeScale.Micro).fontWeight(600)
                .styleModifier { property("text-transform", "uppercase") }.toAttrs()) {
                Text(t("marketing.docs_navigation.on_this_page", "On this page"))
            }
            DocsTocLinks(entries, current, mobile = false)
        }
    }
    TagElement(
        elementBuilder = DetailsElement,
        applyAttrs = DocsContentsStyle.toModifier().toAttrs {
            attr("data-docs-contents", "")
            onKeyDown { event ->
                if (event.key == "Escape") {
                    val details = event.currentTarget as HTMLElement
                    details.setAttribute("data-docs-keyboard", "")
                    details.removeAttribute("open")
                    (details.querySelector("summary") as? HTMLElement)?.focus()
                    event.preventDefault()
                }
            }
            ref { details ->
                val outside = EventListener { event ->
                    if (!details.contains(event.target as? Node)) details.removeAttribute("open")
                }
                document.addEventListener("pointerdown", outside)
                onDispose { document.removeEventListener("pointerdown", outside) }
            }
        },
    ) {
        TagElement(
            elementBuilder = SummaryElement,
            applyAttrs = DocsContentsSummaryStyle.toModifier().toAttrs {
                onClick { event ->
                    val details = (event.currentTarget as HTMLElement).parentElement
                    if ((event.nativeEvent as MouseEvent).detail == 0) details?.setAttribute("data-docs-keyboard", "")
                    else details?.removeAttribute("data-docs-keyboard")
                }
            },
        ) {
            Span(Modifier.styleModifier {
                property("display", "flex")
                property("align-items", "center")
                property("gap", Space.MD.toString())
                property("min-width", "0")
            }.toAttrs()) {
                Span(Modifier.fontSize(TypeScale.Small).styleModifier {
                    property("white-space", "nowrap")
                }.toAttrs()) {
                    Text(t("marketing.docs_navigation.on_this_page", "On this page"))
                }
                Span(Modifier.fontSize(TypeScale.Small).fontWeight(600).styleModifier {
                    property("text-transform", "none")
                    property("white-space", "nowrap")
                    property("overflow", "hidden")
                    property("text-overflow", "ellipsis")
                }.toAttrs { attr("data-docs-current", "") }) { Text(activeLabel) }
            }
            Div(Modifier.iconSize(IconSize.SM).styleModifier {
                property("display", "inline-flex")
                property("flex-shrink", "0")
                property("transition", "rotate var(--docs-motion-duration) ${Motion.EaseOut}")
            }.toAttrs {
                attr("data-docs-chevron", "")
                attr("aria-hidden", "true")
            }) { LChevronDownIcon(Modifier.iconSize(IconSize.SM)) }
        }
        Nav(DocsContentsListStyle.toModifier().toAttrs {
            attr("aria-label", tr("marketing.docs_navigation.on_this_page", "On this page"))
        }) { DocsTocLinks(entries, current, mobile = true) }
    }
}

@Composable
private fun DocsTocLinks(entries: List<DocEntry>, current: String?, mobile: Boolean) {
    val listModifier = Modifier.fillMaxWidth().styleModifier {
        property("list-style", "none")
        property("margin", "0")
        property("padding", "0")
    }
    Ul(listModifier.toAttrs()) {
        entries.forEach { entry ->
            Li {
                DocsTocLink(DocAnchor(entry.id, entry.label), current, mobile)
                if (entry.children.isNotEmpty()) {
                    Ul(listModifier.padding(left = Space.MD).toAttrs()) {
                        entry.children.forEach { child ->
                            Li { DocsTocLink(child, current, mobile) }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun DocsTocLink(anchor: DocAnchor, current: String?, mobile: Boolean) {
    A(href = "#${anchor.id}", attrs = DocsRailLinkStyle.toModifier()
        .then(if (mobile) Modifier.padding(topBottom = Space.SM).minHeight(HIT_AREA_FLOOR_PX.px) else Modifier)
        .toAttrs {
            if (current == anchor.id) attr("aria-current", "location")
            onClick { event ->
                if (event.button.toInt() == 0 && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey) {
                    val destination = document.getElementById(anchor.id) as? HTMLElement
                    if (destination != null) {
                        event.preventDefault()
                        var parent = (event.currentTarget as HTMLElement).parentElement
                        while (parent != null && parent.tagName != "DETAILS") parent = parent.parentElement
                        val pointer = (event.nativeEvent as MouseEvent).detail > 0
                        if (pointer) parent?.removeAttribute("data-docs-keyboard")
                        else parent?.setAttribute("data-docs-keyboard", "")
                        parent?.removeAttribute("open")
                        navigateToDocAnchor(destination, pointer = pointer)
                    }
                }
            }
        },
    ) { Text(anchor.label) }
}

private fun anchorOffset(element: HTMLElement): Double =
    window.getComputedStyle(element).getPropertyValue("scroll-margin-top")
        .removeSuffix("px").toDoubleOrNull() ?: 88.0

private fun navigateToDocAnchor(destination: HTMLElement, pointer: Boolean) {
    val options = js("({})")
    options.left = window.scrollX
    options.top = (window.scrollY + destination.getBoundingClientRect().top - anchorOffset(destination))
        .coerceAtLeast(0.0)
    options.behavior = if (pointer && !window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        "smooth"
    } else {
        "instant"
    }
    if (window.location.hash != "#${destination.id}") window.history.pushState(null, "", "#${destination.id}")
    destination.asDynamic().focus(js("({preventScroll: true})"))
    window.asDynamic().scrollTo(options)
    // Scroll does not fire if the destination is already aligned.
    window.dispatchEvent(org.w3c.dom.events.Event("scroll"))
}
